import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

/**
 * Count total nodes in a tree (root + all descendants).
 */
export function countNodesInTree(node: ConfigTreeNode): number {
  const childCount = (node.children ?? []).reduce(
    (sum, child) => sum + countNodesInTree(child),
    0
  );
  return 1 + childCount;
}

/**
 * Merge two configuration trees by id.
 * When both trees share the same root id, merges children (deduped by id)
 * and recursively merges any matching child nodes.
 * Adjust merge strategy when backend pagination contract is confirmed.
 */
export function mergeConfigurationTrees(
  existing: ConfigTreeNode,
  next: ConfigTreeNode
): ConfigTreeNode {
  if (existing.id !== next.id) {
    return existing;
  }

  const existingChildIds = new Set((existing.children ?? []).map((c) => c.id));
  const mergedChildren: ConfigTreeNode[] = [...(existing.children ?? [])];

  for (const child of next.children ?? []) {
    const existingChild = mergedChildren.find((c) => c.id === child.id);
    if (existingChild) {
      const merged = mergeConfigurationTrees(existingChild, child);
      const idx = mergedChildren.findIndex((c) => c.id === child.id);
      mergedChildren[idx] = merged;
    } else {
      existingChildIds.add(child.id);
      mergedChildren.push(child);
    }
  }

  return { ...existing, children: mergedChildren };
}
